import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaChevronLeft,
  FaCheckCircle,
} from "react-icons/fa";
import toast from "react-hot-toast";
import emailjs from "@emailjs/browser";
import BASE_URL from "../BASEURL";

const RAZORPAY_SCRIPT =
  "https://checkout.razorpay.com/v1/checkout.js";

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existing = document.querySelector(
      `script[src="${RAZORPAY_SCRIPT}"]`
    );

    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Razorpay"))
      );
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () =>
      reject(
        new Error(
          "Payment gateway failed to load. Check your internet connection."
        )
      );
    document.body.appendChild(script);
  });

const parsePaymentSession = (raw) => {
  const root = raw?.data ?? raw;
  const order =
    root?.order ??
    root?.razorpayOrder ??
    raw?.order;

  return {
    key:
      root?.key ??
      root?.key_id ??
      root?.razorpayKey ??
      process.env.REACT_APP_RAZORPAY_KEY,
    orderId:
      root?.orderId ??
      root?.order_id ??
      root?.razorpay_order_id ??
      order?.id ??
      root?.id,
    amount:
      root?.amount ??
      order?.amount,
    currency:
      root?.currency ??
      order?.currency ??
      "INR",
  };
};

/** Razorpay expects paise. Backend may return paise or rupees; avoid 100× double conversion. */
const toRazorpayPaise = (rawAmount, totalRupees) => {
  const expected = Math.round(totalRupees * 100);

  if (rawAmount == null || rawAmount === "") {
    return expected;
  }

  const n = Math.round(Number(rawAmount));
  if (!Number.isFinite(n) || n <= 0) {
    return expected;
  }

  if (n === expected * 100) {
    return expected;
  }

  if (n >= expected * 0.99 && n <= expected * 1.01) {
    return n;
  }

  if (n >= totalRupees * 0.99 && n <= totalRupees * 1.01) {
    return Math.round(n * 100);
  }

  return expected;
};

const parseConvenienceFee = (data) => {
  if (data == null) return 0;
  if (typeof data === "number") return data;

  const candidates = [
    data.convenienceFee,
    data.fee,
    data.paymentFee,
    data.data?.convenienceFee,
    data.settings?.convenienceFee,
    data.paymentSettings?.convenienceFee,
  ];

  for (const value of candidates) {
    if (value === 0 || value === "0" || value) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  return 0;
};

const safeParseJSON = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export default function Checkout() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    houseNo: "",
    area: "",
    city: "",
    state: "",
    pincode: "",
  });

  const [savedAddresses, setSavedAddresses] =
    useState([]);
  const [selectedKey, setSelectedKey] =
    useState(null);
  const [isPaying, setIsPaying] =
    useState(false);
  const [convenienceFeePercent, setConvenienceFeePercent] =
    useState(0);

  // 🔥 GET USER-SPECIFIC CART KEY
  const user = safeParseJSON(
    localStorage.getItem("currentUser")
  );

  const cartKey = user?.email
    ? `cartItems_${user.email}`
    : "cartItems_guest";

  const cartItems =
    safeParseJSON(localStorage.getItem(cartKey)) ||
    [];

  const formatSavedAddress = (addr) => {
    const parts = [
      addr.houseNo,
      addr.area,
      addr.city,
      addr.state,
      addr.pincode,
    ].filter(Boolean);

    return parts.join(", ");
  };

  // 🔥 TOTAL
  const total = cartItems.reduce(
    (sum, item) => {
      const itemPrice =
        item.discountPrice ??
        item.price ??
        0;

      return (
        sum + itemPrice * item.quantity
      );
    },
    0
  );

  const convenienceFeeAmount = Math.round((total * convenienceFeePercent) / 100);
  const finalConvenienceFee = Math.max(0, convenienceFeeAmount);
  const finalTotal = total + finalConvenienceFee;

  // 🔥 FETCH PROFILE + ADDRESSES
  useEffect(() => {
    const token =
      localStorage.getItem("token");

    const fetchProfile = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();

        if (!res.ok) {
          localStorage.removeItem(
            "token"
          );
          navigate("/login");
          return;
        }

        const userData =
          data.user || data;

        // ✅ USER DATA
        setFormData((prev) => ({
          ...prev,
          fullName:
            userData?.name || "",
          email:
            userData?.email || "",
          phone:
            userData?.phone || "",
        }));

        // ✅ ADDRESSES
        const addr =
          userData?.addresses || [];

        setSavedAddresses(addr);

        // ✅ AUTO SELECT FIRST ADDRESS
        if (addr.length > 0) {
          handleSelectAddress(addr[0]);
        }
      } catch (err) {
        console.error(
          "Fetch error:",
          err
        );
      }
    };

    if (token) {
      fetchProfile();
    }

    const fetchFee = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/payment/settings`);
        const data = await res.json();
        if (res.ok) {
          setConvenienceFeePercent(parseConvenienceFee(data));
        }
      } catch (err) {
        console.error("Fetch fee error:", err);
      }
    };
    fetchFee();
  }, []);

  // ✅ SELECT ADDRESS
  const handleSelectAddress = (
    addr
  ) => {
    setSelectedKey(
      addr._id ||
        addr._key ||
        null
    );

    setFormData((prev) => ({
      ...prev,
      fullName:
        addr.fullName || "",
      phone: addr.phone || "",
      houseNo:
        addr.houseNo || "",
      area: addr.area || "",
      city: addr.city || "",
      state:
        addr.state || "",
      pincode:
        addr.pincode || "",
    }));

    toast.success(
      "Selected address"
    );
  };

  // ✍️ INPUT CHANGE
  const handleInputChange = (
    e
  ) => {
    let { name, value } =
      e.target;

    if (name === "phone") {
      value = value
        .replace(/\D/g, "")
        .slice(0, 10);
    }

    if (name === "pincode") {
      value = value
        .replace(/\D/g, "")
        .slice(0, 6);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setSelectedKey(null);
  };

  const finishSuccessfulOrder = () => {
    localStorage.removeItem(cartKey);
    window.dispatchEvent(new Event("cartUpdated"));

    const orderDate = new Date().toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });

    const deliveryAddress = [
      formData.houseNo,
      formData.area,
      formData.city,
      formData.state,
      formData.pincode,
    ]
      .filter(Boolean)
      .join(", ");

    const orderItems = cartItems
      .map((item) => {
        const itemPrice = item.discountPrice ?? item.price ?? 0;
        return `- ${item.name} x ${item.quantity} = ₹${itemPrice * item.quantity}`;
      })
      .join("\n");

    const orderItemsHtml = cartItems
      .map((item) => {
        const itemPrice = item.discountPrice ?? item.price ?? 0;
        return `<li style="margin-bottom:8px;">${item.name} x ${item.quantity} — ₹${itemPrice * item.quantity}</li>`;
      })
      .join("");

    const orderHtml = `
<div style="font-family: Arial, Helvetica, sans-serif; background:#f4f6f8; padding:30px;">
  <div style="max-width:600px; margin:auto; background:#ffffff; border-radius:12px; overflow:hidden; box-shadow:0 2px 10px rgba(0,0,0,0.08);">
    <div style="background:#0f172a; padding:25px; text-align:center;">
      <h1 style="color:#ffffff; margin:0; font-size:24px;">🛒 Order Confirmation</h1>
      <p style="color:#cbd5e1; margin-top:8px;">Thank you for your order!</p>
    </div>
    <div style="padding:30px;">
      <h2 style="color:#1e293b; margin-top:0;">Hello ${formData.fullName || "Customer"},</h2>
      <p style="color:#475569; line-height:1.6;">We have successfully received your order. Here are the details:</p>
      <div style="background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:20px; margin:20px 0;">
        <h3 style="margin-top:0; color:#0f172a;">📋 Order Summary</h3>
        <table width="100%" cellpadding="6" cellspacing="0">
          <tr>
            <td style="color:#64748b; font-weight:600;">Order Date</td>
            <td style="color:#1e293b;">${orderDate}</td>
          </tr>
          <tr>
            <td style="color:#64748b; font-weight:600;">Total Amount</td>
            <td style="color:#16a34a; font-weight:bold;">₹${finalTotal}</td>
          </tr>
          <tr>
            <td style="color:#64748b; font-weight:600;">Phone</td>
            <td style="color:#1e293b;">${formData.phone}</td>
          </tr>
          <tr>
            <td style="color:#64748b; font-weight:600;">Delivery Address</td>
            <td style="color:#1e293b;">${deliveryAddress}</td>
          </tr>
        </table>
      </div>
      <div style="margin-top:25px;">
        <h3 style="color:#0f172a;">🛍 Ordered Items</h3>
        <div style="background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:15px; color:#334155; line-height:1.8;">
          <ul style="padding-left:18px; margin:0;">${orderItemsHtml}</ul>
        </div>
      </div>
      <div style="margin-top:30px; padding:20px; background:#ecfdf5; border-left:4px solid #22c55e; border-radius:6px;">
        <p style="margin:0; color:#166534;">Your order is being processed. We will contact you shortly regarding delivery updates.</p>
      </div>
      <p style="margin-top:30px; color:#475569;">Thank you for shopping with us ❤️</p>
    </div>
    <div style="background:#f8fafc; text-align:center; padding:15px; color:#94a3b8; font-size:12px; border-top:1px solid #e2e8f0;">
      © 2025 Your Store. All rights reserved.
    </div>
  </div>
</div>
    `;

    const message = `
*New Order Placed*

Name: ${formData.fullName}
Phone: ${formData.phone}

Address:
${deliveryAddress}

Items:
${orderItems}

Total: ₹${finalTotal}
`;

    const whatsappURL = `https://wa.me/9904444990?text=${encodeURIComponent(
      message
    )}`;

    const emailParams = {
      to_name: formData.fullName || "Customer",
      customer_name: formData.fullName,
      customer_email: formData.email || "Guest",
      customer_phone: formData.phone,
      order_date: orderDate,
      total_amount: finalTotal,
      delivery_address: deliveryAddress,
      order_items: orderItems,
      order_message: message,
      order_html: orderHtml,
    };

    emailjs
      .send(
        "service_r9xlc6k",
        "template_y470rkl",
        emailParams,
        "3CN5qUzrqdUIDuhr5"
      )
      .then(() => {
        console.log("EmailJS order email sent");
      })
      .catch((emailErr) => {
        console.error("EmailJS send failed:", emailErr);
      });

    toast.success("Order placed successfully!");
    window.open(whatsappURL, "_blank");

    setTimeout(() => {
      navigate("/");
    }, 1000);
  };

const openRazorpayCheckout = (
  paymentData,
  token,
  mongoOrderId
) => {
    const session =
      parsePaymentSession(paymentData);

    if (!session.orderId || !session.key) {
      console.error(
        "Invalid create-order response:",
        paymentData
      );
      throw new Error(
        "Could not start Razorpay. Backend must return key and order id."
      );
    }

    const amountPaise = toRazorpayPaise(
      session.amount,
      finalTotal
    );

    if (amountPaise < 100) {
      throw new Error(
        "Order total must be at least ₹1 to pay online."
      );
    }

    const options = {
      key: session.key,
      amount: amountPaise,
      currency: session.currency,
      name: "Holly Zolly",
      description: "Order payment",
      order_id: session.orderId,
      prefill: {
        name: formData.fullName,
        email: formData.email,
        contact: formData.phone,
      },
      theme: { color: "#ea580c" },
      handler: async (response) => {
        try {
          const verifyHeaders = {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        const verifyRes = await fetch(
          `${BASE_URL}/api/payment/verify-payment`,
          {
            method: "POST",
            headers: verifyHeaders,
            body: JSON.stringify({
              razorpay_payment_id:
                response.razorpay_payment_id,
              razorpay_order_id:
                response.razorpay_order_id,
              razorpay_signature:
                response.razorpay_signature,
              mongoOrderId,
            }),
          }
        );

          const verifyData =
            await verifyRes.json();

          if (!verifyRes.ok) {
            throw new Error(
              verifyData.message ||
                "Payment verification failed"
            );
          }

          finishSuccessfulOrder();
        } catch (verifyErr) {
          console.error(verifyErr);
          toast.error(
            verifyErr.message ||
              "Payment verification failed"
          );
        } finally {
          setIsPaying(false);
        }
      },
      modal: {
        ondismiss: () => {
          setIsPaying(false);
          toast.error(
            "Payment cancelled. You can try again when ready."
          );
        },
      },
    };

    const rzp = new window.Razorpay(options);

    rzp.on("payment.failed", (response) => {
      setIsPaying(false);
      const msg =
        response.error?.description ||
        response.error?.reason ||
        "Payment failed. Please try again.";
      toast.error(msg);
    });

    rzp.open();
    setIsPaying(false);
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    const currentUser = safeParseJSON(
      localStorage.getItem("currentUser")
    );

    const token =
      localStorage.getItem("token");

    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      navigate("/cart");
      return;
    }

    if (
      !formData.fullName ||
      !formData.phone ||
      !formData.houseNo ||
      !formData.area ||
      !formData.city ||
      !formData.state ||
      !formData.pincode
    ) {
      toast.error(
        "Please fill all required fields"
      );
      return;
    }

    if (isPaying) return;

    setIsPaying(true);

    try {
      if (!selectedKey && token) {
        const addressRes = await fetch(
          `${BASE_URL}/api/auth/add-address`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              fullName: formData.fullName,
              phone: formData.phone,
              houseNo: formData.houseNo,
              area: formData.area,
              city: formData.city,
              state: formData.state,
              pincode: formData.pincode,
            }),
          }
        );

        const addressData =
          await addressRes.json();

        if (!addressRes.ok) {
          throw new Error(
            addressData.message ||
              "Failed to save address"
          );
        }

        const updatedUser =
          addressData.user || addressData;

        setSavedAddresses(
          updatedUser.addresses || []
        );
      }

      const orderPayload = {
        ...(currentUser?._id && { userId: currentUser._id }),
        products: cartItems.map((item) => ({
          productId: item._id,
          quantity: item.quantity,
          size: item.selectedSize,
        })),
        totalPrice: finalTotal,
        paymentMethod: "Razorpay",
        address: {
          fullName: formData.fullName,
          phone: formData.phone,
          pincode: formData.pincode,
          state: formData.state,
          city: formData.city,
          houseNo: formData.houseNo,
          area: formData.area,
        },
      };

      await loadRazorpayScript();

      const createHeaders = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const createRes = await fetch(
        `${BASE_URL}/api/payment/create-order`,
        {
          method: "POST",
          headers: createHeaders,
          body: JSON.stringify(orderPayload),
        }
      );

      let paymentData;
      try {
        paymentData = await createRes.json();
      } catch {
        throw new Error(
          "Payment server returned an invalid response."
        );
      }

      if (!createRes.ok) {
        throw new Error(
          paymentData?.message ||
            "Could not start payment"
        );
      }

     openRazorpayCheckout(
  paymentData,
  token,
  paymentData.mongoOrderId
);
    } catch (err) {
      console.error(err);
      setIsPaying(false);
      toast.error(
        err.message || "Failed to place order"
      );
    }
  };
  return (
    <section className="py-16 bg-[#FCFBFA] min-h-screen">
      <div className="max-w-7xl mx-auto px-6">

        {/* HEADER */}
        <div className="mb-12">
          <button
            onClick={() =>
              navigate("/cart")
            }
            className="flex items-center gap-2 text-gray-400 hover:text-black mb-4"
          >
            <FaChevronLeft size={12} />
            Back to Cart
          </button>

          <h1 className="text-4xl font-bold text-black">
            Secure{" "}
            <span className="text-orange-600 italic">
              Checkout
            </span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* LEFT */}
          <div className="lg:col-span-2 space-y-8">

            {/* SAVED ADDRESS */}
            {savedAddresses.length >
              0 && (
              <div className="bg-white rounded-3xl p-8 border shadow-sm">
                <h3 className="font-bold text-xl mb-6 flex items-center gap-3">
                  <FaMapMarkerAlt className="text-orange-600" />
                  Select Saved
                  Address
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {savedAddresses.map(
                    (
                      addr,
                      index
                    ) => {
                      const key =
                        addr._id ||
                        index;

                      return (
                        <div
                          key={
                            key
                          }
                          onClick={() =>
                            handleSelectAddress(
                              addr
                            )
                          }
                          className={`cursor-pointer p-4 rounded-2xl border-2 ${
                            selectedKey ===
                            key
                              ? "border-orange-500 bg-orange-50"
                              : "border-gray-100"
                          }`}
                        >
                          <div className="flex justify-between">
                            <span className="font-bold text-orange-600">
                              {
                                addr.fullName
                              }
                            </span>

                            {selectedKey ===
                              key && (
                              <FaCheckCircle className="text-orange-600" />
                            )}
                          </div>

                          <p className="text-sm text-gray-600 mt-2">
                            {formatSavedAddress(
                              addr
                            )}
                          </p>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}

            {/* FORM */}
            <div className="bg-white rounded-3xl p-8 border shadow-sm">
              <h3 className="font-bold text-xl mb-6">
                Delivery Details
              </h3>

              <form className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input
                  name="fullName"
                  value={
                    formData.fullName
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Full Name"
                  className="input-style"
                />

                <input
                  name="phone"
                  value={
                    formData.phone
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Phone"
                  className="input-style"
                />

                <input
                  name="houseNo"
                  value={
                    formData.houseNo
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="House No"
                  className="input-style"
                />

                <input
                  name="area"
                  value={
                    formData.area
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Area"
                  className="input-style"
                />

                <input
                  name="city"
                  value={
                    formData.city
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="City"
                  className="input-style"
                />

                <input
                  name="state"
                  value={
                    formData.state
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="State"
                  className="input-style"
                />

                <input
                  name="pincode"
                  value={
                    formData.pincode
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Pincode"
                  className="input-style md:col-span-2"
                />
              </form>
            </div>
          </div>

          {/* RIGHT */}
          <aside>
            <div className="bg-black text-white rounded-3xl p-8 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">
                Order Summary
              </h3>

              <div className="space-y-4 mb-6">
                {cartItems.map(
                  (
                    item,
                    index
                  ) => {
                    const itemPrice =
                      item.discountPrice ??
                      item.price ??
                      0;

                    return (
                      <div
                        key={
                          index
                        }
                        className="flex justify-between text-sm"
                      >
                        <span>
                          {
                            item.name
                          }{" "}
                          x{" "}
                          {
                            item.quantity
                          }
                        </span>

                        <span className="text-orange-400">
                          ₹
                          {itemPrice *
                            item.quantity}
                        </span>
                      </div>
                    );
                  }
                )}
              </div>

              {finalConvenienceFee > 0 && (
                <div className="flex justify-between text-sm mb-4">
                  <span>Convenience Fee ({convenienceFeePercent}%)</span>
                  <span className="text-orange-400">₹{finalConvenienceFee}</span>
                </div>
              )}

              <div className="flex justify-between text-xl font-bold">
                <span>Total</span>
                <span className="text-orange-500">
                  ₹{finalTotal}
                </span>
              </div>

              <button
                type="button"
                onClick={handlePlaceOrder}
                disabled={isPaying}
                className="w-full mt-6 bg-orange-600 py-3 rounded-xl font-bold disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isPaying
                  ? "Processing..."
                  : "Pay & Place Order"}
              </button>
            </div>
          </aside>
        </div>
      </div>

      <style>{`
        .input-style {
          width: 100%;
          background: #f9fafb;
          border-radius: 14px;
          padding: 12px 16px;
          border: 1px solid #eee;
          outline: none;
        }

        .input-style:focus {
          border: 1px solid #ea580c;
          background: white;
        }
      `}</style>
    </section>
  );
}